import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../utils/apiClient";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  FileText,
  Upload,
  Trash2,
  Loader2,
  ArrowLeft,
  File,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

export default function AgentDocuments() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const res = await apiClient.get("/agent/documents");
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "image/png",
      "image/jpeg",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported file type. Use PDF, DOCX, TXT, or images.");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum 50MB.");
      return;
    }

    setUploading(true);
    setUploadProgress("Uploading...");

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploadProgress("Processing document...");
      const res = await apiClient.post("/agent/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(`Document uploaded: ${res.data.chunk_count} sections indexed`);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (docId, docName) => {
    try {
      await apiClient.delete(`/agent/documents/${docId}`);
      toast.success(`Deleted: ${docName}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <FileText className="w-5 h-5 text-red-500" />;
      case "docx":
        return <FileText className="w-5 h-5 text-blue-500" />;
      case "image":
        return <File className="w-5 h-5 text-green-500" />;
      default:
        return <File className="w-5 h-5 text-muted-foreground" />;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="agent-documents-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/agent/chat")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold">Documents</h1>
              <p className="text-muted-foreground text-sm">
                Upload and manage your contracts and agreements
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition ${
            uploading
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          }`}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="upload-area"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
            data-testid="file-input"
          />

          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
              <p className="font-medium">{uploadProgress}</p>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Click to upload or drag and drop</p>
              <p className="text-sm text-muted-foreground mt-1">
                PDF, DOCX, TXT, or images (max 50MB)
              </p>
            </>
          )}
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 border border-border rounded-lg bg-card">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-medium">No documents yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload your first contract or agreement to get started
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-card border border-border rounded-lg"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {getFileIcon(doc.file_type)}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{doc.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {doc.chunk_count} sections
                      {doc.page_count ? ` · ${doc.page_count} pages` : ""}
                      {doc.is_ocr && " · OCR"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400 rounded flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Indexed
                  </span>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete "{doc.name}" and all its indexed content. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(doc.id, doc.name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-200">
                What can you upload?
              </p>
              <ul className="mt-1 text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Collective bargaining agreements</li>
                <li>Union contracts and policies</li>
                <li>Letters of understanding</li>
                <li>Schedules and appendices</li>
                <li>Grievance documentation</li>
                <li>Work rules and HR policies</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
